import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Cog, Cloud, Hand } from "lucide-react";
import { useAdapter, useSnapshot, useTopology } from "@/store/site";
import { useNow } from "@/hooks/useNow";
import { MODE_CATALOG, MODE_TONE } from "@/data/modeCatalog";
import { Card } from "@/components/ui/Card";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import { formatHourMin, formatRelativeAge, formatRelativeFuture } from "@/lib/format";

// Source wire values stay the data; the visible label is translated at
// render. Unknown sources fall through t() unchanged (t returns its key).
const SOURCE_LABEL_KEY: Record<string, string> = {
  cloud: "monitor.mode.sourceCloud",
  local: "monitor.mode.sourceLocal",
  manual_override: "monitor.mode.sourceManual",
};

/**
 * "Why is the controller doing what it's doing?" — answers in <2 s.
 * Operator-first: shows mode source, entry name, time-window, and the next
 * mode change with a live countdown. If a manual override is active, it
 * dominates the visual hierarchy so it can't be missed.
 */
export function ModeReasoningCard() {
  const adapter = useAdapter();
  const topology = useTopology();
  const snap = useSnapshot();
  const t = useT();
  const now = useNow(1000);
  // Schedule source via React Query. The old hand-rolled `setInterval(…, 5000)`
  // mounted in the same commit as HealthBanner's 5 s alarm poll, so the two
  // fired on the SAME frame — the main-thread hit behind the Overview's "stuck
  // moment every 5 seconds". A 20 s interval (de-phased from the 15 s alarms
  // poll) keeps the mode-reasoning fresh without that aligned double-poll.
  const { data: src = null } = useQuery({
    queryKey: ["schedule-source", topology.id],
    queryFn: () => adapter.schedule(),
    refetchInterval: 20_000,
    staleTime: 15_000,
  });

  const overrideActive = snap.controller.schedule_source === "manual_override";
  const tone = MODE_TONE[snap.controller.active_mode] ?? MODE_TONE.gridZeroControl;
  const entry = src?.active_now.entry;
  const next = src?.next_change;
  const overrideExpires = snap.controller.override_expires_at;

  return (
    <Card className={cn("relative overflow-hidden p-4", overrideActive && "border-warn/50 ring-1 ring-warn/30")}>
      {overrideActive && (
        <div className="pointer-events-none absolute inset-0 -z-0 opacity-30" style={{
          background: "repeating-linear-gradient(45deg, color-mix(in srgb, var(--ems-warn) 12%, transparent), color-mix(in srgb, var(--ems-warn) 12%, transparent) 10px, transparent 10px, transparent 20px)",
        }} />
      )}
      <div className="relative flex items-start gap-3">
        <span className={cn("inline-grid h-10 w-10 shrink-0 place-items-center rounded-lg",
          overrideActive ? "bg-warn/15 text-warn" : "bg-accent/15 text-accent",
        )}>
          {overrideActive ? <Hand size={20} /> : <Cog size={20} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("nav.controller")}</span>
            <span
              className={cn("rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider", tone)}
              title={MODE_CATALOG[snap.controller.active_mode]?.summary}
            >
              {MODE_CATALOG[snap.controller.active_mode]?.label ?? snap.controller.active_mode}
            </span>
            {overrideActive && (
              <span className="rounded-md border border-warn/40 bg-warn/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warn">
                {t("monitor.mode.sourceManual")}
              </span>
            )}
            {snap.controller.pid_actively_controlling && !overrideActive && (
              <span className="flex items-center gap-1 rounded-md border border-ok/40 bg-ok/10 px-1.5 py-0.5 text-[10px] text-ok">
                <span className="h-1.5 w-1.5 animate-pulse-pip rounded-full bg-ok" /> {t("monitor.mode.tracking")}
              </span>
            )}
          </div>
          <div className="mt-1.5 text-sm text-text">
            {overrideActive ? (
              <>
                {t("monitor.mode.overrideActive")}{" "}
                {overrideExpires && (
                  <>{t("monitor.mode.expires")} <strong className="text-text">{formatHourMin(overrideExpires)}</strong>{" "}
                    <span className="text-text-muted">({formatRelativeFuture(overrideExpires - now)})</span></>
                )}
              </>
            ) : entry ? (
              <>
                {t("monitor.mode.following")} <strong className="text-text">{entry.name}</strong>{" "}
                <span className="text-text-muted">({src ? t(SOURCE_LABEL_KEY[src.source] ?? src.source) : ""} · {formatTimeWindow(entry.start_min, entry.end_min, t("monitor.mode.wrapsMidnight"))})</span>
              </>
            ) : (
              <>{t("monitor.mode.noEntry")}</>
            )}
          </div>
          <div className="mt-1 grid grid-cols-1 gap-1.5 text-[11px] text-text-muted sm:grid-cols-2">
            <div>
              <span className="text-text-faint">{t("monitor.mode.source")}</span>
              <div className="flex items-center gap-1 text-text">
                {snap.controller.schedule_source === "cloud" && <Cloud size={11} />}
                {snap.controller.schedule_source === "manual_override" && <Hand size={11} />}
                {t(SOURCE_LABEL_KEY[snap.controller.schedule_source] ?? snap.controller.schedule_source)}
              </div>
            </div>
            <div>
              <span className="text-text-faint">{t("monitor.mode.nextChange")}</span>
              <div className="text-text">
                {next ? (
                  <>
                    <span className="num">{formatHourMin(next.at)}</span>
                    <span className="ml-1 text-text-muted">({formatRelativeFuture(next.at - now)})</span>
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </div>
        </div>
        <Link to={carryTo("/schedule")} className="ml-auto flex h-8 shrink-0 items-center gap-1 rounded-md border border-edge bg-surface px-2.5 text-[11px] hover:border-accent/40">
          {t("nav.schedule")} <ArrowRight size={11} />
        </Link>
      </div>
    </Card>
  );
}

// `wrapsLabel` is the pre-translated "(wraps midnight)" suffix — passed in
// from the render site so this helper stays hook-free.
function formatTimeWindow(startMin: number, endMin: number, wrapsLabel: string) {
  const fmt = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  return `${fmt(startMin)}–${fmt(endMin)}${startMin > endMin ? ` ${wrapsLabel}` : ""}`;
}
